import { useEffect, useRef, useState } from "react";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { API_URL } from "../utils/api";

const ELIDO_LOGO = "https://customer-assets.emergentagent.com/job_custom-demo-gen/artifacts/x2wfas84_Elido_logotype_blue.png";

export default function VerifyPage() {
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("token")
    || new URLSearchParams(window.location.search).get("token"));
  const [status, setStatus] = useState("verifying");
  const [error, setError] = useState("");
  const verification = useRef(null);

  useEffect(() => {
    let active = true;
    let redirectTimer;
    window.history.replaceState(window.history.state, "", window.location.pathname);
    if (!token) {
      setStatus("error");
      setError("Ingen inloggningstoken hittades.");
      return;
    }
    // React StrictMode may replay the effect. Share the promise instead of
    // submitting the same one-time token twice from this component.
    if (!verification.current) {
      verification.current = fetch(`${API_URL}/api/auth/verify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ token }),
      }).then(async (response) => {
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(typeof data.detail === "string" ? data.detail : "Inloggningen misslyckades.");
        }
        return data;
      });
    }
    verification.current.then(() => {
      if (!active) return;
      setStatus("success");
      redirectTimer = window.setTimeout(() => window.location.replace("/admin"), 1000);
    }).catch((failure) => {
      if (!active) return;
      setStatus("error");
      setError(failure instanceof TypeError ? "Kunde inte ansluta. Försök igen." : failure.message);
    });
    return () => {
      active = false;
      window.clearTimeout(redirectTimer);
    };
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src={ELIDO_LOGO} 
            alt="Elido" 
            className="h-12 mx-auto"
          />
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {status === "verifying" && (
            <>
              <div className="w-16 h-16 bg-[#255E69]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-8 h-8 text-[#255E69] animate-spin" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Verifierar inloggning...
              </h2>
              <p className="text-gray-500">
                Vänta medan vi loggar in dig.
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Inloggning lyckades!
              </h2>
              <p className="text-gray-500">
                Du omdirigeras till admin...
              </p>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Inloggningen misslyckades
              </h2>
              <p className="text-gray-500 mb-6">
                {error}
              </p>
              <button
                onClick={() => window.location.href = "/login"}
                className="px-6 py-3 bg-[#255E69] text-white font-medium rounded-xl hover:bg-[#1e4a53] transition-colors"
              >
                Tillbaka till inloggning
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
